import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClinic } from "@/context/ClinicContext";
import { useIdleLogout } from "@/lib/useIdleLogout";
import { Home, Pill, FlaskConical, CreditCard, Clock, LogOut, MessageSquare, HeartPulse, ShieldCheck , CalendarPlus, CalendarDays, LineChart} from "lucide-react";
import { cn } from "@/lib/utils";
import RosMark from "@/components/RosMark";
import ClinicMark from "@/components/ClinicMark";

const NAV = [
  { to: "/portal", label: "Home", icon: Home, end: true, testid: "pnav-home" },
  { to: "/portal/book", label: "Book", icon: CalendarPlus, testid: "nav-book" },
  { to: "/portal/appointments", label: "Appointments", icon: CalendarDays, testid: "pnav-appointments" },
  { to: "/portal/treatment", label: "My Plan", icon: Pill, testid: "pnav-treatment" },
  { to: "/portal/labs", label: "Labs", icon: FlaskConical, testid: "pnav-labs" },
  { to: "/portal/checkin", label: "Check-in", icon: HeartPulse, testid: "pnav-checkin" },
  { to: "/portal/progress", label: "My weight", icon: LineChart, testid: "pnav-progress" },
  { to: "/portal/messages", label: "Messages", icon: MessageSquare, testid: "pnav-messages" },
  { to: "/portal/membership", label: "Membership", icon: CreditCard, testid: "pnav-membership" },
  { to: "/portal/timeline", label: "Journey", icon: Clock, testid: "pnav-timeline" },
  { to: "/portal/privacy", label: "Privacy", icon: ShieldCheck, testid: "pnav-privacy" },
];

export default function PatientLayout({ children }) {
  const { user, logout } = useAuth();
  const { clinic } = useClinic();
  const navigate = useNavigate();
  const clinicName = clinic?.name || "Your Clinic";

  // Patient sessions auto-logoff after 30 minutes of inactivity (HIPAA).
  useIdleLogout({ minutes: 30, onIdle: () => navigate("/patient/login?expired=1") });

  const handleLogout = async () => { await logout(); navigate("/patient/login"); };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-card border-b border-border sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center overflow-hidden">
              <ClinicMark className="text-sm" />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-bold text-sm leading-tight truncate" data-testid="portal-clinic-name">{clinicName}</p>
              <p className="text-[11px] text-muted-foreground">Patient Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block" data-testid="portal-patient-name">{user?.name}</span>
            <button data-testid="portal-logout" onClick={handleLogout} className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-all" title="Sign out">
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
        {/* Top nav (desktop + mobile horizontal) */}
        <nav className="max-w-3xl mx-auto px-2 flex gap-1 overflow-x-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors",
                  isActive ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                )
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-6">{children}</main>

      <footer className="py-4 text-[11px] text-muted-foreground flex items-center justify-center gap-1.5">
        <RosMark size={12} /> Powered by RelationshipOS
      </footer>
    </div>
  );
}
